import type { AccountView, CardView, StatementLine, Valuation } from "../api/types.js";
import type { IconId } from "../lib/icon.js";
import { cardDue } from "./card-due.js";
import {
  dateTime,
  dayLabel,
  differenceMoney,
  formatMoney,
  gainSinceCost,
  holdingValue,
  lastDigits,
  shareCount,
  shareOf,
  signedAmount,
  signedMoney,
  signedPercent,
  statementDate,
  sumMoney,
} from "../lib/format.js";

/**
 * Home's figures, from the bank's own reads (UX spec v3 §6.1).
 *
 * Every figure here is either a string the bank returned or an exact sum or
 * product of such strings (`lib/format.ts`). Two things the design shows are
 * NOT computed because the bank does not return what they need: the Investments
 * total across currencies (no FX read on the portfolio) and a day-change
 * percentage (no previous close). Both are README asks, not guesses.
 */
export interface HoldingRead {
  readonly instrument_id: string;
  readonly units: string;
  readonly average_cost: string;
  readonly currency: string;
  readonly name: string;
  readonly ticker: string;
  readonly price: string | undefined;
}

export interface LedgerRead extends StatementLine {
  readonly account_id: string;
}

export interface HomeReads {
  readonly name: string;
  readonly initials: string;
  readonly accounts: readonly AccountView[];
  readonly cards: readonly CardView[];
  readonly holdings: readonly HoldingRead[];
  readonly recent: readonly LedgerRead[];
  /**
   * P1-2: for a reservation on Recent, when its bundle's held step goes out — from that
   * bundle's receipt (`hold_until`), bundle id → ISO time; `null` = the receipt holds
   * nothing any more (cancelled or gone out), so the reservation line is history and
   * Recent leaves it to Activity. Absent: the line reads "Held".
   */
  readonly holds?: ReadonlyMap<string, string | null>;
  /** `GET /v1/portfolio/valuation` (ask n): the bank's own total, across currencies (P2-11). */
  readonly valuation?: Valuation | undefined;
  /** The bank's `as_of` on `/v1/accounts` (DCTA-77 ask bb): the month an account's detail is for. */
  readonly asOf?: string | undefined;
}

/**
 * One product on Home (§C.4, `.acct`): the figure first, the name with its digits under
 * it, then only what the product carries — card thumbnails, what is due, the limit, the
 * gain and the cash waiting to be invested. `key` is what the detail (13) opens.
 */
export interface Cell {
  readonly key: string;
  readonly kind: "account" | "joint" | "credit" | "invest";
  readonly icon: IconId;
  readonly amount: string;
  readonly name: string;
  /** Printed in ink-2 after the name: "···4417", "···6012 · owed", "· 2 holdings". */
  readonly digits: string;
  readonly cards: readonly { readonly digits: string; readonly kind: "debit" | "credit" }[];
  /** The all-time change: "up" (gain), "down" (loss) or "flat" — QA D8: a change that rounds to 0.0% is neither. */
  readonly pill?: { readonly text: string; readonly tone: "up" | "down" | "flat" };
  readonly due?: { readonly amount: string; readonly by: string };
  readonly meter?: { readonly available: string; readonly limit: string; readonly used: string };
  readonly gain?: string;
  readonly settlement?: { readonly key: string; readonly name: string; readonly digits: string; readonly amount: string };
}

export interface HomeModel {
  readonly name: string;
  readonly initials: string;
  /** DCTA-231 V2: the customer locked their money — any account the bank reads is locked (lock_funds locks them all). */
  readonly locked: boolean;
  readonly total: string;
  readonly investments: string | undefined;
  readonly cells: readonly Cell[];
  readonly holdings: readonly {
    readonly ticker: string;
    readonly name: string;
    readonly sub: string;
    readonly value: string;
    readonly gain: string;
    readonly up: boolean;
  }[];
  readonly recent: readonly {
    readonly initial: string;
    readonly name: string;
    readonly sub: string;
    readonly figure: string;
    readonly credit: boolean;
    /** Activity's `.muted` amount: money reserved, not gone. */
    readonly muted: boolean;
    /** Activity's `.st` part of the sub-line ("Held until 24 Sep 09:00"). */
    readonly status?: string;
  }[];
}

const RANK: Readonly<Record<string, number>> = { savings: 0, current: 1, joint_savings: 2, investment_settlement: 3 };

/**
 * What a ledger line's account is called: a deposit account by its display name; a credit
 * card's own account in the book (`card_<card_id>`, DCTA-77 ask y) by the card's name.
 */
export function accountNames(accounts: readonly AccountView[], cards: readonly CardView[]): Map<string, string> {
  const names = new Map(accounts.map((a) => [a.account_id, a.display_name]));
  for (const c of cards) if (c.type === "credit" && c.display_name !== undefined) names.set(`card_${c.card_id}`, c.display_name);
  return names;
}

/** Home's account order (savings → current → joint → settlement), shared with Activity's filters (P2-2). */
export function byAccountRank<T extends { readonly type: string }>(accounts: readonly T[]): T[] {
  return [...accounts].sort((a, b) => (RANK[a.type] ?? 9) - (RANK[b.type] ?? 9));
}
const ICON: Readonly<Record<string, IconId>> = {
  savings: "i-bank",
  current: "i-bank",
  joint_savings: "i-users",
  investment_settlement: "i-trend",
};

/** "•••• •••• •••• 3306" → "3306": a thumbnail's last four (the registry's own digits). */
const four = (masked: string): string => lastDigits(masked).replace(/^···/, "");

const positive = (money: string): boolean => /^\d+\.\d{2}$/.test(money) && !/^0+\.00$/.test(money);

function accountCell(a: AccountView, cards: readonly CardView[]): Cell {
  return {
    key: a.account_id,
    kind: a.type === "joint_savings" ? "joint" : "account",
    icon: ICON[a.type] ?? "i-bank",
    amount: formatMoney(a.balance, a.currency),
    name: a.display_name,
    digits: lastDigits(a.masked_number),
    cards: cards.filter((c) => c.type === "debit" && c.account_id === a.account_id).map((c) => ({ digits: four(c.masked_pan), kind: "debit" as const })),
  };
}

function creditCell(c: CardView): Cell {
  const currency = c.currency ?? "SGD";
  const owed = c.current_balance as string;
  const available = c.credit_limit === undefined ? undefined : differenceMoney(c.credit_limit, owed);
  const minimum = cardDue(c).minimum;
  return {
    key: c.card_id,
    kind: "credit",
    icon: "i-card",
    amount: formatMoney(owed, currency),
    name: c.display_name ?? "Card",
    digits: `${lastDigits(c.masked_pan)} · owed`,
    cards: [{ digits: four(c.masked_pan), kind: "credit" }],
    // DCTA-131: the minimum still DUE (the bank's `minimum_due_now`) — once it is met there is no minimum to make.
    ...(minimum !== undefined && positive(minimum) && c.due_date !== undefined
      ? { due: { amount: formatMoney(minimum, currency), by: `by ${statementDate(c.due_date)}` } }
      : {}),
    ...(c.credit_limit !== undefined && available !== undefined && !available.startsWith("-")
      ? { meter: { available: formatMoney(available, currency), limit: formatMoney(c.credit_limit, currency), used: shareOf(owed, c.credit_limit) } }
      : {}),
  };
}

function investCell(v: Valuation, settlement: AccountView | undefined, count: number): Cell {
  const pct = v.totals.unrealised_pl_pct === undefined ? undefined : signedPercent(v.totals.unrealised_pl_pct);
  return {
    key: "investments",
    kind: "invest",
    icon: "i-trend",
    amount: formatMoney(v.totals.market_value, v.base_currency),
    name: "Investments",
    digits: `· ${String(count)} ${count === 1 ? "holding" : "holdings"}`,
    cards: [],
    ...(pct === undefined ? {} : { pill: { text: pct, tone: pct.startsWith("+") ? "up" : pct.startsWith("\u2212") ? "down" : "flat" } }),
    ...(v.totals.unrealised_pl === undefined ? {} : { gain: signedAmount(v.totals.unrealised_pl, v.base_currency) }),
    ...(settlement === undefined
      ? {}
      : {
          settlement: {
            key: settlement.account_id,
            name: settlement.display_name,
            digits: lastDigits(settlement.masked_number),
            amount: formatMoney(settlement.balance, settlement.currency),
          },
        }),
  };
}

/**
 * Home's cells in Home's order: the deposit accounts (savings → current → joint), the
 * credit cards, then Investments with the settlement cash inside it. Without the bank's
 * valuation there is no honest Investments figure, so the settlement account stands as
 * its own cell. A product the bank does not return has no cell — no gap, no placeholder.
 */
export function cellsOf(accounts: readonly AccountView[], cards: readonly CardView[], valuation: Valuation | undefined, holdings: number): Cell[] {
  const ranked = byAccountRank(accounts);
  const settlement = ranked.find((a) => a.type === "investment_settlement");
  const deposits = ranked.filter((a) => valuation === undefined || a !== settlement);
  const before = deposits.filter((a) => a.type !== "investment_settlement").map((a) => accountCell(a, cards));
  const after = deposits.filter((a) => a.type === "investment_settlement").map((a) => accountCell(a, cards));
  const credit = cards.filter((c) => c.type === "credit" && c.current_balance !== undefined).map(creditCell);
  const held = valuation?.holdings?.length ?? holdings;
  // QA D5: no trading account and nothing held — no Investments cell (an empty "S$0.00 · 0 holdings" tile is not a product).
  const invest = valuation === undefined || (settlement === undefined && held === 0) ? [] : [investCell(valuation, settlement, held)];
  return [...before, ...credit, ...invest, ...after];
}

export function homeModel(reads: HomeReads, nowMs: number = Date.now()): HomeModel {
  const accounts = byAccountRank(reads.accounts);
  const sgd = accounts.filter((a) => a.currency === "SGD");
  const names = accountNames(reads.accounts, reads.cards);

  const holdings = [...reads.holdings]
    .filter((h) => h.price !== undefined)
    .sort((a, b) => Number(b.currency === "SGD") - Number(a.currency === "SGD") || a.name.localeCompare(b.name));
  const currencies = new Set(holdings.map((h) => h.currency));
  const values = holdings.map((h) => holdingValue(h.units, h.price as string));

  return {
    name: reads.name,
    initials: reads.initials,
    locked: reads.accounts.some((a) => a.locked),
    total: formatMoney(sumMoney(sgd.map((a) => a.balance)), "SGD"),
    // P2-11: the valuation read's total when the bank gives it (across currencies, its own FX);
    // else, only a single-currency sum of the bank's own figures — never an invented FX total.
    investments:
      reads.valuation !== undefined
        ? formatMoney(reads.valuation.totals.market_value, reads.valuation.base_currency)
        : holdings.length > 0 && currencies.size === 1
        ? formatMoney(sumMoney(values), holdings[0]?.currency ?? "SGD")
        : undefined,
    cells: cellsOf(accounts, reads.cards, reads.valuation, reads.holdings.length),
    holdings: holdings.map((h, i) => {
      const gain = gainSinceCost(h.price as string, h.average_cost);
      return {
        ticker: h.ticker,
        name: h.name,
        sub: `${shareCount(h.units)} · ${formatMoney(h.price as string, h.currency)}`,
        value: formatMoney(values[i] as string, h.currency),
        gain,
        up: gain.startsWith("+"),
      };
    }),
    // A line with no name the customer could read (a reservation posts with
    // neither counterparty nor reference, ask o) is left to Activity, which
    // shows the whole ledger — Home is a glance, and a blank row says nothing.
    //
    // P1-2 (DCTA-75): Activity's rule on Home too — a reservation is money waiting, not
    // money gone: its figure is muted and unsigned and its sub-line says "Held until …";
    // the reservation's own bookkeeping (released, applied) is not a movement at all.
    recent: [...reads.recent]
      .filter((r) => (r.counterparty_name ?? r.reference ?? "") !== "")
      .filter((r) => r.entry_type !== "reservation_released" && r.entry_type !== "reservation_applied")
      .filter((r) => !(r.entry_type === "reservation" && r.bundle_id !== undefined && reads.holds?.get(r.bundle_id) === null))
      .sort((a, b) => Date.parse(b.posted_at) - Date.parse(a.posted_at))
      .slice(0, 3)
      .map((r) => {
        const name = r.counterparty_name ?? r.reference ?? "";
        const sub = `${dayLabel(r.posted_at, nowMs)} · ${names.get(r.account_id) ?? ""}`;
        const initial = /[A-Za-z]/.exec(name)?.[0]?.toUpperCase() ?? "·";
        if (r.entry_type === "reservation") {
          const until = r.bundle_id === undefined ? undefined : (reads.holds?.get(r.bundle_id) ?? undefined);
          return {
            initial,
            name,
            sub,
            status: until === undefined ? "Held" : `Held until ${dateTime(until)}`,
            figure: formatMoney(r.amount, r.currency),
            credit: false,
            muted: true,
          };
        }
        return { initial, name, sub, figure: signedMoney(r.amount, r.currency, r.direction), credit: r.direction === "credit", muted: false };
      }),
  };
}
